package com.zi.support.shared

/** A camera lens on the back, in mm from the back's top-left corner (as you look at the back). */
data class DeviceLens(val xMm: Float, val yMm: Float, val radiusMm: Float)

/** A raised camera island on the back, same coordinates as [DeviceLens]. */
data class DeviceIsland(val xMm: Float, val yMm: Float, val widthMm: Float, val heightMm: Float, val radiusMm: Float, val color: Long)

/**
 * What a phone or tablet looks like, for the 3D model in More > Apps on web, Android and iOS: real size (mm), corner radius,
 * colours (0xRRGGBB), the screen's border, rear cameras and the buttons on its right edge (seen from the front).
 */
data class DeviceShape(
    val name: String,
    val tablet: Boolean,
    val widthMm: Float,
    val heightMm: Float,
    val depthMm: Float,
    val cornerMm: Float,
    val body: Long,
    val frame: Long,
    val bezelMm: Float,
    val lenses: List<DeviceLens>,
    val island: DeviceIsland? = null,
    /** Front camera: a punch hole near the top centre (phones) or a dot in the bezel (tablets). */
    val frontCameraMm: Float = 1.6f,
    /** Right-edge buttons as (top, length) in mm from the top. */
    val buttons: List<Pair<Float, Float>> = emptyList(),
    /** True when [name] comes from the table below rather than a generic shape. */
    val exact: Boolean = true,
    /** Screen border above and below the screen when thicker than [bezelMm] (handheld PDAs, rugged phones with a chin). */
    val bezelTopMm: Float = bezelMm,
    val bezelBottomMm: Float = bezelMm,
    /** Rugged phones: rubber corner guards this many mm proud of the frame, in [accent]. */
    val bumperMm: Float = 0f,
    /** Bumpers, left-edge keys and scanner trim colour (0xRRGGBB); null = the frame colour. */
    val accent: Long? = null,
    /** Barcode-scanner window across the top edge, this many mm wide (0 = none). */
    val scannerMm: Float = 0f,
    /** Left-edge keys seen from the front (scan / push-to-talk) as (top, length) in mm, drawn in [accent]. */
    val leftButtons: List<Pair<Float, Float>> = emptyList(),
) {
    /** Front camera sits in the top border (tablets, PDAs) rather than as a punch hole in the screen. */
    val cameraInBezel: Boolean get() = tablet || bezelTopMm >= 8f
}

object DeviceShapes {
    const val SCREEN: Long = 0x0B1220L

    // ponytail: a short table of the models in the fleet; anything else gets a generic phone/tablet of the right
    // kind. Add a line when a new model shows up (sizes from the maker's spec sheet).
    private val MODELS: List<Pair<(String) -> Boolean, DeviceShape>> = listOf(
        { m: String -> m.startsWith("SM-X21") } to DeviceShape("Galaxy Tab A9+", true, 168.7f, 257.1f, 6.9f, 11f, 0x3C3F44L, 0x4A4E54L, 8.5f,
            listOf(DeviceLens(12f, 12f, 3.6f)), frontCameraMm = 1.4f, buttons = listOf(24f to 10f, 40f to 22f)),
        { m: String -> m.startsWith("SM-A36") } to DeviceShape("Galaxy A36 5G", false, 78.2f, 162.9f, 7.4f, 10f, 0x232427L, 0x2E3034L, 2.2f,
            listOf(DeviceLens(16f, 15f, 5.2f), DeviceLens(16f, 28.5f, 5.2f), DeviceLens(16f, 42f, 5.2f)),
            island = DeviceIsland(8.5f, 6.5f, 15f, 44f, 7.5f, 0x2C2E32L), buttons = listOf(38f to 22f, 64f to 12f)),
        { m: String -> m.startsWith("SM-A") || m.startsWith("SM-M") } to DeviceShape("Galaxy A series", false, 77.5f, 162f, 8.2f, 10f, 0x26282CL, 0x30333AL, 2.4f,
            listOf(DeviceLens(14f, 13f, 4.6f), DeviceLens(14f, 25f, 4.6f), DeviceLens(14f, 37f, 4.6f)), buttons = listOf(38f to 22f, 64f to 12f)),
        { m: String -> m.startsWith("SM-S") } to DeviceShape("Galaxy S series", false, 70.6f, 147f, 7.6f, 10f, 0x1E1F22L, 0x8A8D93L, 1.8f,
            listOf(DeviceLens(13f, 12f, 5f), DeviceLens(13f, 25f, 5f), DeviceLens(13f, 38f, 5f)), buttons = listOf(32f to 20f, 56f to 12f)),
        { m: String -> m.startsWith("CPH26") || m.startsWith("CPH25") } to DeviceShape("OPPO A3 / A40", false, 76.1f, 165.8f, 7.7f, 10.5f, 0x1F2023L, 0x2A2C30L, 3f,
            listOf(DeviceLens(16f, 15.5f, 5f), DeviceLens(16f, 30f, 4f)),
            island = DeviceIsland(7.5f, 6.5f, 17f, 33f, 7f, 0x2A2C30L), buttons = listOf(36f to 22f, 64f to 12f)),
        // ponytail: rugged/PDA sizes from GSMArena and Hikvision's DS-MDT202 datasheet; camera layout approximated from product photos.
        { m: String -> m.startsWith("ARMOR X13") } to DeviceShape("Ulefone Armor X13", false, 83f, 173f, 13.6f, 9f, 0x1B1C1EL, 0x2A2C2FL, 4f,
            listOf(DeviceLens(32f, 15f, 5.4f), DeviceLens(51f, 15f, 5.4f)),
            island = DeviceIsland(20f, 6f, 43f, 18f, 6f, 0x111214L), frontCameraMm = 1.5f,
            buttons = listOf(42f to 14f, 62f to 9f), bezelTopMm = 6f, bezelBottomMm = 9f, bumperMm = 1.6f, accent = 0xF2661FL,
            leftButtons = listOf(48f to 14f)),
        { m: String -> m.startsWith("ARMOR X16") } to DeviceShape("Ulefone Armor X16", false, 83.4f, 173.8f, 18f, 9.5f, 0x2C2F2DL, 0x393D3AL, 4f,
            listOf(DeviceLens(30f, 17f, 6f), DeviceLens(52f, 17f, 6f)),
            island = DeviceIsland(17f, 6.5f, 49f, 21f, 7f, 0x161817L), frontCameraMm = 1.5f,
            buttons = listOf(40f to 14f, 60f to 9f), bezelTopMm = 6f, bezelBottomMm = 9f, bumperMm = 2f, accent = 0x6B7366L,
            leftButtons = listOf(46f to 16f)),
        { m: String -> m.startsWith("ARMOR") } to DeviceShape("Ulefone Armor", false, 82f, 172f, 15f, 9f, 0x1B1C1EL, 0x2A2C2FL, 4f,
            listOf(DeviceLens(32f, 15f, 5.2f), DeviceLens(50f, 15f, 5.2f)), island = DeviceIsland(20f, 6f, 42f, 18f, 6f, 0x111214L),
            buttons = listOf(42f to 14f, 62f to 9f), bezelTopMm = 6f, bezelBottomMm = 9f, bumperMm = 1.6f, accent = 0xF2661FL, leftButtons = listOf(48f to 14f)),
        // Hikvision handheld terminal: 5.5" screen in a thick border, scanner window on the top edge, yellow scan keys on both sides.
        { m: String -> m.startsWith("DS-MDT") } to DeviceShape("Hikvision DS-MDT202", false, 74.6f, 160f, 15.4f, 9f, 0x2B2E33L, 0x1F2125L, 5.5f,
            listOf(DeviceLens(37.3f, 22f, 4.2f)), island = DeviceIsland(25f, 12f, 24.6f, 20f, 6f, 0x17191CL), frontCameraMm = 1.2f,
            buttons = listOf(58f to 18f, 30f to 10f), bezelTopMm = 15f, bezelBottomMm = 21f, bumperMm = 1.2f, accent = 0xF5A623L,
            scannerMm = 30f, leftButtons = listOf(58f to 18f)),
        { m: String -> m.startsWith("IPAD") } to DeviceShape("iPad", true, 179.5f, 247.6f, 7f, 12f, 0xC9CCD1L, 0xB9BCC2L, 9f,
            listOf(DeviceLens(11f, 11f, 3.6f)), buttons = listOf(18f to 14f, 36f to 14f)),
        { m: String -> m.startsWith("IPHONE") } to DeviceShape("iPhone", false, 71.5f, 147.6f, 7.8f, 11f, 0x2F3033L, 0x9EA1A6L, 2f,
            listOf(DeviceLens(12.5f, 12.5f, 5.6f), DeviceLens(24.5f, 24.5f, 5.6f)),
            island = DeviceIsland(5.5f, 5.5f, 26f, 26f, 6.5f, 0x3A3B3FL), buttons = listOf(30f to 18f)),
    )

    /** Samsung tablets are SM-T/SM-X/SM-P; others say Tab, Pad or iPad in the model. */
    fun isTablet(manufacturer: String, model: String): Boolean {
        val m = model.trim().uppercase()
        return m.startsWith("SM-T") || m.startsWith("SM-X") || m.startsWith("SM-P") || m.startsWith("TB-") ||
            listOf("TAB", "PAD").any { it in m } || manufacturer.contains("ipad", ignoreCase = true)
    }

    fun of(manufacturer: String, model: String, platform: String): DeviceShape {
        val key = model.trim().uppercase().ifBlank { if (platform.equals("ios", true)) "IPHONE" else "" }
        MODELS.firstOrNull { it.first(key) }?.second?.let { return it }
        val brand = manufacturer.trim().replaceFirstChar { it.uppercase() }
        val name = listOf(brand, model.trim()).filter { it.isNotBlank() }.joinToString(" ").ifBlank { "Unknown device" }
        // Unknown Ulefone / Hikvision models still look rugged / like a handheld terminal, just not to the mm.
        val family = when {
            manufacturer.contains("ulefone", true) -> "ARMOR"
            manufacturer.contains("hikvision", true) -> "DS-MDT"
            else -> null
        }
        family?.let { f -> MODELS.first { it.first(f) }.second.let { return it.copy(name = name, exact = false) } }
        return if (isTablet(manufacturer, model)) DeviceShape(name, true, 165f, 254f, 7f, 11f, 0x3A3F47L, 0x474C55L, 9f,
            listOf(DeviceLens(12f, 12f, 3.6f)), buttons = listOf(24f to 10f, 40f to 22f), exact = false)
        else DeviceShape(name, false, 75f, 162f, 8.2f, 10f, 0x2B2F36L, 0x363A42L, 2.4f,
            listOf(DeviceLens(14f, 14f, 4.8f), DeviceLens(14f, 27f, 4.8f)), buttons = listOf(36f to 22f, 62f to 12f), exact = false)
    }

    fun of(device: SupportDevice): DeviceShape = of(device.field("manufacturer"), device.model, device.platform)

    /** "#2B2F36" for CSS. */
    fun hex(color: Long): String = "#" + color.toString(16).padStart(6, '0').uppercase()
}

/** One line of the popup: [label] and its [value]; [flag] marks it (e.g. an outdated version). */
data class DeviceFact(val label: String, val value: String, val flag: Boolean = false)

/**
 * What the device popup in More > Apps shows, worked out once for web, Android and iOS so all three match:
 * the title, the status chip, four tiles, the "Connection" rows, the history and every raw field.
 */
data class DevicePopup(
    val title: String,
    val subtitle: String,
    val state: String,
    val stateOn: Boolean,
    val tiles: List<DeviceFact>,
    val rows: List<DeviceFact>,
    val history: List<DeviceHistoryEntry>,
    /** Only NotificationCentre apps keep a device history; the others hide that section. */
    val hasHistory: Boolean,
    val fields: List<DeviceDetail>,
    val shape: DeviceShape,
) {
    companion object {
        fun of(app: String, device: SupportDevice, devices: List<SupportDevice>, nowMillis: Long): DevicePopup {
            val shape = DeviceShapes.of(device)
            val modelLine = if (shape.exact) "${shape.name} · ${device.model}" else shape.name
            val outdated = AppInstalls.isOutdated(device, devices)
            val centre = AppInstalls.notificationCentreKey(app) != null
            val (state, on) = if (centre) AppInstalls.stateLabel(device) to device.active
                else if (DeviceStatus.isOnline(device, nowMillis)) "Online" to true else "Offline" to false
            fun ago(millis: Long) = if (millis <= 0) "Never" else DeviceStatus.ago(millis, nowMillis).replaceFirstChar { it.uppercase() }
            val user = when {
                centre -> device.deviceId
                app == AppInstalls.DRIVER_HUB -> device.deviceId
                else -> device.owner
            }
            val company = when {
                centre -> device.owner
                app == AppInstalls.DRIVER_HUB -> device.owner
                else -> ""
            }
            return DevicePopup(
                title = modelLine,
                subtitle = listOf(device.number.ifBlank { if (app == AppInstalls.SUPPORT_APP) device.deviceId else "" }, app).filter { it.isNotBlank() }.joinToString(" · "),
                state = state,
                stateOn = on,
                tiles = listOf(
                    DeviceFact("Version", AppInstalls.versionLabel(device) + if (outdated) " · update" else "", outdated),
                    DeviceFact("Last opened", ago(device.lastOpenedMillis)),
                    DeviceFact("Last seen", ago(device.lastSeenMillis)),
                    DeviceFact(if (app == AppInstalls.SUPPORT_APP) "Platform" else "Company", if (app == AppInstalls.SUPPORT_APP) platformLabel(device) else company.ifBlank { "—" }),
                ),
                rows = listOf(
                    DeviceFact(if (app == AppInstalls.SUPPORT_APP) "Signed in as" else "User", user.ifBlank { "Not logged in" }),
                    DeviceFact("Server", DeviceStatus.serverLabel(device.server)),
                    DeviceFact("Device", listOf(device.field("manufacturer").replaceFirstChar { it.uppercase() }, device.model).filter { it.isNotBlank() }.joinToString(" ")),
                    // Support App shows the platform as a tile already.
                    DeviceFact("Platform", if (app == AppInstalls.SUPPORT_APP) "" else platformLabel(device)),
                ).filter { it.value.isNotBlank() },
                history = device.history,
                hasHistory = centre,
                fields = device.details,
                shape = shape,
            )
        }

        private fun platformLabel(device: SupportDevice) = when (device.platform.lowercase()) {
            "android" -> "Android"; "ios" -> "iOS"; "web" -> "Web"; else -> device.platform.replaceFirstChar { it.uppercase() }
        }
    }
}

/** A field of the device's record ("" when it hasn't one). */
fun SupportDevice.field(key: String): String = details.firstOrNull { it.label == key }?.value.orEmpty()
